import { Tooltip, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import type { FC } from 'react';
import { useMemo } from 'react';

import type { StatusIdE } from 'declarations/enums/tickets';
import type { RelatedTicketInfoI } from 'declarations/interfaces/tickets';
import { formatAmount } from 'helpers/locale';

import { mapAgeToColor } from '../../../../helpers';

interface ROTicketTabsPropsT {
  currentTicketId: string;
  currentTicketInfo: RelatedTicketInfoI;
  relatedTickets: RelatedTicketInfoI[];
  onTabChange: (ticketId: string) => void;
  isLoading?: boolean;
}

const buildTabLabel = (ticket: RelatedTicketInfoI): string => `#${ticket.number}`;

const statusDisplayName = (statusId: StatusIdE): string => String(statusId);

const buildTooltipContent = (ticket: RelatedTicketInfoI): React.ReactNode => (
  <Box display="flex" flexDirection="column" gap={1} p={0.5}>
    <Box display="flex" alignItems="center" gap={1}>
      <Typography variant="caption" color="common.white" fontWeight="bold">
        Status:
      </Typography>
      <Typography variant="body2" fontWeight="bold">
        {statusDisplayName(ticket.statusId)}
      </Typography>
    </Box>
    <Box display="flex" alignItems="center" gap={1}>
      <Typography variant="caption" color="common.white" fontWeight="bold">
        Amount:
      </Typography>
      <Typography variant="body2" fontWeight="bold">
        {formatAmount(ticket.amount)}
      </Typography>
    </Box>
    <Box display="flex" alignItems="center" gap={1}>
      <Typography variant="caption" color="common.white" fontWeight="bold">
        Assignee:
      </Typography>
      <Typography variant="body2" fontWeight="bold">
        {ticket.assigneeName}
      </Typography>
    </Box>
    <Box display="flex" alignItems="center" gap={1}>
      <Typography variant="caption" color="common.white" fontWeight="bold">
        Age:
      </Typography>
      <Typography variant="body2" fontWeight="bold" color={mapAgeToColor(ticket.age)}>
        {ticket.age}
      </Typography>
    </Box>
  </Box>
);

const ROTicketTabs: FC<ROTicketTabsPropsT> = ({
  currentTicketId,
  currentTicketInfo,
  relatedTickets,
  onTabChange,
  isLoading = false,
}) => {
  const allTickets = useMemo((): RelatedTicketInfoI[] => {
    const merged = [currentTicketInfo, ...relatedTickets];
    const seen = new Set<string>();
    const deduped: RelatedTicketInfoI[] = [];
    for (const t of merged) {
      if (!seen.has(t.id)) {
        seen.add(t.id);
        deduped.push(t);
      }
    }
    deduped.sort((a, b): number => a.number - b.number);
    return deduped;
  }, [currentTicketInfo, relatedTickets]);

  const activeTabIndex = useMemo((): number => {
    const idx = allTickets.findIndex((t): boolean => t.id === currentTicketId);
    return idx >= 0 ? idx : 0;
  }, [allTickets, currentTicketId]);

  if (allTickets.length <= 1) return null;

  const handleTabClick = (_event: React.SyntheticEvent, newValue: number): void => {
    const selected = allTickets[newValue];
    if (selected && selected.id !== currentTicketId) {
      onTabChange(selected.id);
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        zIndex: 2,
        opacity: isLoading ? 0.6 : 1,
        transition: 'opacity 0.2s',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        backgroundColor: (theme: any) => theme.palette.background.paper,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        boxShadow: (theme: any): string =>
          theme.palette.mode === 'dark'
            ? `0px 2px 8px ${theme.palette.primary.main}40`
            : '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 0.5,
          display: 'flex',
          alignItems: 'center',
          minHeight: '28px',
        }}
      >
        <Tabs
          value={activeTabIndex}
          onChange={handleTabClick}
          aria-label="RO ticket tabs"
          sx={{
            minHeight: '24px',
            borderBottom: 'none',
            '& .MuiTabs-indicator': {
              display: 'block',
              height: '3px',
              borderRadius: '3px 3px 0 0',
              backgroundColor: 'primary.main',
            },
          }}
        >
          {allTickets.map(
            (ticket): JSX.Element => (
              <Tooltip
                key={ticket.id}
                title={buildTooltipContent(ticket)}
                arrow
                placement="bottom"
                componentsProps={{
                  tooltip: {
                    sx: {
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      bgcolor: (theme: any) => theme.palette.grey[800],
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      color: (theme: any) => theme.palette.common.white,
                    },
                  },
                  arrow: {
                    sx: {
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      color: (theme: any) => theme.palette.grey[800],
                    },
                  },
                }}
              >
                <Tab
                  label={
                    <Typography component="span" sx={{ fontSize: 'inherit' }}>
                      {buildTabLabel(ticket)}
                    </Typography>
                  }
                  sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                    textTransform: 'none',
                    minHeight: '24px',
                    py: 0.5,
                    border: 'none',
                    backgroundColor: 'transparent',
                    marginBottom: 0,
                    zIndex: 'auto',
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    color: (theme: any) => theme.palette.text.primary,
                    '&.Mui-selected': {
                      backgroundColor: 'transparent',
                      marginBottom: 0,
                      zIndex: 'auto',
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      color: (theme: any) => theme.palette.primary.main,
                    },
                  }}
                />
              </Tooltip>
            )
          )}
        </Tabs>
      </Box>
    </Box>
  );
};

export default ROTicketTabs;
